<script module lang="ts">
  import type { VoiceOption } from './suggest';
  // Voices are per-community and stable; fetch once and share across every composer
  // instance (there can be many — one under each post and comment).
  const voicesCache = new Map<number, VoiceOption[]>();
</script>

<script lang="ts">
  /**
   * The reply composer: a textarea, the AI "Suggest" flow (voice picker + cited
   * sources), and the action that queues what was written.
   *
   * ONE COMPOSER. There were two — 384 lines in the web app, 397 in the Helper, the
   * second a port of the first — and they had drifted in every direction at once:
   * different routes for the same two calls, different padding, one clearing the box
   * after queueing and one not, error lines in a different order. The differences
   * that were REAL are three: what the request carries (transport, `host.apiFetch`),
   * what a failure should do (navigate or explain, `host.apiErrorMessage`), and who
   * the reply goes out as (`host.postsAs`).
   *
   * A fourth used to be whether the box emptied afterwards, because the dashboard held
   * the text back for editing. Nothing edits a sent reply now — the box always clears,
   * which is also the answer that cannot invite sending the same thing twice.
   *
   * Everything else was the same component written twice.
   */
  import { tick } from 'svelte';
  import Avatar from '../Avatar.svelte';
  import { followLink, ui } from '../host';
  import { fetchVoices, streamSuggestedReply, type SourceRef } from './suggest';
  import {
    isOnlyMentions,
    linkMentions,
    MENTIONS_IN_DMS,
    searchMentionCandidates,
    type MentionCandidate,
  } from './mentions';

  let {
    skoolId,
    kind,
    targetId,
    onSubmit = null,
    submitLabel = 'Reply',
    placeholder = 'Write your reply…',
    doneLabel = 'Sent',
    oncancel = null,
    autofocus: doFocus = true,
    onStartWriting = null,
    participants = [],
    submitOnEnter = false,
    leading,
    jumpWhenOffscreen = false,
    addressee = null,
  }: {
    skoolId: number | null;
    /**
     * What is being replied to. `dm` is a conversation rather than a row in a
     * community, so its `targetId` is the channel's external_id and its `skoolId` is
     * only there for the voice picker and the mention roster — see the DM call site.
     */
    kind: 'post' | 'comment' | 'dm';
    targetId: number | string;
    /** Receives the text, and the people actually PICKED from the mention menu — the
     *  only ones with an `external_id` to file a mention under. A host that links
     *  mentions on the way out spends the second argument; one that queues can ignore
     *  it. Deliberately not "every name that matches a member": linking a name the
     *  owner typed themselves would tag a stranger who happens to share it. */
    /**
     * Publish this reply, whatever that means on this surface — the panel posts it to
     * Skool there and then, the dashboard queues it for a Helper to send. There is no
     * third state: nothing here saves a draft, and the label says which of the two
     * actually happens.
     */
    onSubmit?:
      | ((text: string, mentions: MentionCandidate[]) => Promise<string | void> | string | void)
      | null;
    submitLabel?: string;
    /** What the empty box invites. A DM is not a reply to anything, and a box saying
     *  "reply" in a drawer opened to START a conversation describes the wrong act. */
    placeholder?: string;
    /** The confirmation, past tense — and NOT the same word as `submitLabel`, on
     *  purpose. The button names what the person is doing ("Reply"); this names what
     *  actually happened to it ("Queued" on the dashboard, "Posted" in the panel).
     *  Queueing is how the dashboard replies, not a different act, and a button that
     *  says "Queue" asks someone to think about our plumbing to answer a member. Only the default — a submit that knows better says so by
     *  RETURNING its own word, which is how the panel distinguishes a reply that went
     *  straight to Skool from one that fell back to the queue. */
    doneLabel?: string;
    oncancel?: (() => void) | null;
    autofocus?: boolean;
    /** Fired the first time the owner puts the cursor in the box. The Helper uses it
     *  to bring the Skool tab to the thing being replied to. Per-target, so it stays
     *  a prop rather than a host capability. */
    onStartWriting?: (() => void) | null;
    /**
     * The people already in this conversation — a post's author and its commenters,
     * or the two ends of a DM.
     *
     * They are offered FIRST and with no request, because they are who a reply almost
     * always addresses and they are already in hand. The roster search behind them
     * widens the net to anyone in the community; see `searchMentionCandidates`.
     */
    participants?: MentionCandidate[];
    /** Enter sends, Shift+Enter breaks the line — the convention every chat has, and
     *  wrong for a long-form reply to a post. The mention menu still takes Enter first
     *  while it is open, which is what makes picking a name feel native. */
    submitOnEnter?: boolean;
    /**
     * Controls for the LEFT of the action row, where a host has actions that belong
     * beside sending rather than in a bar of their own — the inbox puts Back and
     * "Mark as read" here, so the three things anyone does to a row sit on one line.
     */
    leading?: import('svelte').Snippet | undefined;
    /**
     * Offer a way back to this box once it has scrolled out of sight.
     *
     * For a thread you scroll THROUGH — the inbox, where the reply box sits under one
     * comment in the middle of forty and reading the rest of them leaves it behind. A
     * chat pane, where the composer is pinned at the bottom and never leaves, wants
     * nothing of the sort, so this is opt-in.
     */
    jumpWhenOffscreen?: boolean;
    /**
     * Whoever wrote the thing being answered. Given, the box opens with them already
     * mentioned — the way Skool's own reply box does, and the way nearly every reply in
     * a thread starts.
     *
     * It is a real mention, not decoration: they are seeded into `picked`, so the plain
     * `@Name` typed here becomes `[@Name](obj://user/<id>)` on send and actually
     * notifies them. Deleting it from the box is enough to take it back — the rewrite
     * only replaces text that is still there.
     */
    addressee?: MentionCandidate | null;
  } = $props();

  // Callers remount (via {#key}) for a new target, so these seed once and are then the
  // writer's to change.
  // svelte-ignore state_referenced_locally
  /**
   * The box starts addressed to whoever is being answered.
   *
   * `$state` seeded once, not `$derived`: this is a starting point the writer then owns,
   * and a derived value would type over them every time the prop settled.
   */
  const opener = addressee?.name || addressee?.handle || '';
  /** What the box opens with, kept so "has anything been written?" stays answerable —
   *  the seed is not writing, and every control that keyed off "is it empty" was
   *  silently disabled the moment the box started pre-filled. */
  const SEED = opener ? `@${opener} ` : '';
  let replyText = $state(SEED);
  /** The composer's own element, for the "is it still on screen" question below. */
  let root = $state<HTMLElement | null>(null);
  let offscreen = $state(false);
  /**
   * FALLBACK anchor for the jump button. `--sg-float-left` wins when it is set.
   *
   * A `fixed` element anchors to the viewport, and the viewport is not the content
   * area everywhere: the dashboard puts a `lg:block` sidebar down the left, so a plain
   * `left-5` parked this pill on top of the account block at the bottom of the nav. No
   * static offset fixes that — the sidebar collapses between `w-60` and `w-14`.
   *
   * INSIDE `<InboxDeck>` the answer comes from the deck instead, as a custom property
   * inherited down the DOM. That is deliberate rather than lazy: the deck floats its
   * own "Done with Post" against the opposite edge of the SAME column, and the two
   * only look like a pair if one box measures both edges. Measuring locally is what
   * made them asymmetric — this composer sits inside the card, so its own left edge
   * carries the card's padding and the pill sat further in than its partner sat out.
   *
   * The local measurement stays as the fallback for every other host, where there is
   * no deck and this rect is the best thing available. Custom properties inherit, so
   * neither side needs a prop and a host that knows nothing about either still works.
   */
  let anchorLeft = $state(20);
  /** What the last submit reported, past tense, where it reported anything. */
  let said = $state<string | null>(null);
  let suggesting = $state(false);
  let suggestError = $state<string | null>(null);
  let sources = $state<SourceRef[]>([]);
  let showSources = $state(false);
  let saving = $state(false);
  let saved = $state(false);
  // A REFUSED SUBMIT MUST NOT READ AS DONE. `onSubmit` can genuinely fail —
  // 409 while the Helper holds that exact reply mid-send, 400 when the Skool
  // addressing can't be resolved — and a dropped reply that looks sent is the one
  // failure this composer must never have. So the tick is conditional on the write
  // actually landing, and the server's own sentence is shown when it didn't.
  let saveError = $state<string | null>(null);

  let voices = $state<VoiceOption[]>([]);
  let selectedVoice = $state<number | null>(null);

  $effect(() => {
    if (skoolId != null) void loadVoices(skoolId);
  });

  // Who actually publishes this, and anything standing in its way.
  const identity = $derived(onSubmit ? ui().postsAs() : null);
  const authorName = $derived(identity?.name ?? null);
  /**
   * The verb is the SURFACE's, the name is only whose voice it goes out in.
   *
   * These were fused, and it made the button lie: with an identity known it read
   * "Post" and tipped "Posts to Skool as X" on the dashboard too — where nothing is
   * posted, a row is queued for a Helper to send later. Whether this publishes or
   * queues is the host's fact, so `submitLabel` says it and the identity chip rides
   * alongside instead of overwriting it.
   */
  const postTip = $derived(
    authorName
      ? `${submitLabel} — goes out to Skool as ${authorName}`
      : `${submitLabel} — your Helper posts it to Skool`,
  );
  // Its own line, and only when a reply won't go out as-is. The happy path says
  // nothing: the send button's label and title already name the poster.
  const warn = $derived(identity?.warning ?? null);

  /** One reason to show, or nothing at all when the host took over (navigated). */
  function reason(err: unknown, fallback: string): string | null {
    return ui().apiErrorMessage(err, fallback);
  }

  function pickVoice(list: VoiceOption[], id: number): number | null {
    if (list.length === 0) return null;
    let savedPick: number | null = null;
    try {
      const s = localStorage.getItem(`sg_voice_${id}`);
      savedPick = s ? Number(s) : null;
    } catch {
      /* ignore */
    }
    if (savedPick != null && list.some((v) => v.member_id === savedPick)) return savedPick;
    const chosen = list.find((v) => v.is_default) ?? list[0];
    return chosen ? chosen.member_id : null;
  }

  async function loadVoices(id: number): Promise<void> {
    const cached = voicesCache.get(id);
    if (cached) {
      voices = cached;
      selectedVoice = pickVoice(cached, id);
      return;
    }
    try {
      const list = await fetchVoices(id);
      voicesCache.set(id, list);
      voices = list;
      selectedVoice = pickVoice(list, id);
    } catch (err: unknown) {
      // Fail soft: hide the picker. Suggesting still works, in the default voice.
      reason(err, '');
      voices = [];
    }
  }

  function onVoiceChange(memberId: number): void {
    selectedVoice = memberId;
    if (skoolId == null) return;
    try {
      localStorage.setItem(`sg_voice_${skoolId}`, String(memberId));
    } catch {
      /* ignore */
    }
  }

  // Voice picker (a custom menu, not a native <select>, so it can carry each voice's
  // face and sit flush against the Suggest button as one control).
  let voiceMenuOpen = $state(false);
  let pickerEl = $state<HTMLDivElement | null>(null);
  const selectedVoiceOption = $derived(voices.find((v) => v.member_id === selectedVoice) ?? null);
  const voiceLabel = $derived(
    selectedVoiceOption?.name || (selectedVoiceOption?.handle ? '@' + selectedVoiceOption.handle : 'Voice'),
  );
  function chooseVoice(memberId: number): void {
    onVoiceChange(memberId);
    voiceMenuOpen = false;
  }
  // Dismiss the open menu on an outside click or Escape.
  $effect(() => {
    if (!voiceMenuOpen) return;
    const onDown = (e: PointerEvent): void => {
      if (pickerEl && !pickerEl.contains(e.target as Node)) voiceMenuOpen = false;
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') voiceMenuOpen = false;
    };
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('keydown', onKey);
    };
  });

  // Focus the reply box on mount; rAF wins the race with any parent focus.
  function autofocus(node: HTMLTextAreaElement): { destroy: () => void } {
    if (!doFocus) return { destroy: () => {} };
    const id = requestAnimationFrame(() => node.focus());
    return { destroy: () => cancelAnimationFrame(id) };
  }

  /** Once per mount. The composer is remounted per target (see the {#key} at the call
   *  sites), so "once" means once per thread, not once per session — and clicking
   *  back into the box after a stray blur must not announce again. */
  let announcedWriting = false;
  function handleFocus(): void {
    if (announcedWriting) return;
    announcedWriting = true;
    onStartWriting?.();
  }

  // --- the @-mention picker ---------------------------------------------------
  //
  // Skool renders a mention only when the text carries `[@Name](obj://user/<id>)`, and
  // an id is not something anyone can type. So the owner types `@` and a name, picks a
  // person, and `linkMentions` turns the plain text into the real thing on the way out
  // (see `./mentions`). Picking is what supplies the id; the rewrite is what spends it.
  //
  // ANCHORED UNDER THE BOX, not at the caret. Caret coordinates in a textarea mean
  // mirroring its content into a hidden div to measure — a lot of machinery, and a
  // second thing to keep in step with the textarea's own styling, for a menu that is
  // readable either way in a 340px panel.

  let box = $state<HTMLTextAreaElement | null>(null);
  let mentionOpen = $state(false);
  /** Index of the `@` being completed, so the insert knows what to replace. */
  let mentionAt = $state(0);
  let mentionHits = $state<MentionCandidate[]>([]);
  let mentionIndex = $state(0);
  let mentionSeq = 0;
  /** Everyone chosen from the menu in this composer's life, plus whoever it opened
   *  addressed to. Kept even if the owner edits the name afterwards — `linkMentions`
   *  matches on the text, so a stale candidate simply never matches and costs nothing. */
  // svelte-ignore state_referenced_locally
  let picked = $state<MentionCandidate[]>(addressee ? [addressee] : []);

  /** Can this target carry a mention at all? A DM cannot — see `MENTIONS_IN_DMS`. The
   *  menu never opens rather than opening and inserting something Skool would show as
   *  raw markup. */
  const mentionable = $derived(kind !== 'dm' || MENTIONS_IN_DMS);

  /** The `@word` immediately before the caret, or null. A mention starts at a word
   *  boundary (so `hello@example.com` is not one) and stops at whitespace — a name
   *  with a space in it is reached by typing its first word and picking. */
  function mentionUnderCaret(el: HTMLTextAreaElement): { at: number; q: string } | null {
    const caret = el.selectionStart ?? 0;
    const upto = el.value.slice(0, caret);
    const m = /(^|[^A-Za-z0-9_@])@([^\s@]{0,30})$/.exec(upto);
    if (!m) return null;
    return { at: caret - (m[2]?.length ?? 0) - 1, q: m[2] ?? '' };
  }

  function closeMentions(): void {
    mentionOpen = false;
    mentionHits = [];
  }

  /** Participants first, then the roster — deduplicated by id, so the person being
   *  replied to never appears twice and never sits below a stranger. */
  async function refreshMentions(q: string): Promise<void> {
    const seq = ++mentionSeq;
    const needle = q.trim().toLowerCase();
    const local = participants.filter(
      (p) =>
        p.externalId &&
        (!needle ||
          (p.name ?? '').toLowerCase().includes(needle) ||
          (p.handle ?? '').toLowerCase().includes(needle)),
    );
    let merged = local;
    // One character is not a search, it is every member of the community. Wait for two
    // before spending a request; the participants above already answer the common case.
    if (skoolId != null && needle.length >= 2) {
      try {
        const remote = await searchMentionCandidates(skoolId, q.trim());
        if (seq !== mentionSeq) return; // a later keystroke owns the menu now
        const seen = new Set(local.map((p) => p.externalId));
        merged = [...local, ...remote.filter((r) => r.externalId && !seen.has(r.externalId))];
      } catch {
        // A failed lookup narrows the menu to the participants; it never closes it.
      }
    }
    if (seq !== mentionSeq) return;
    mentionHits = merged.slice(0, 8);
    mentionIndex = 0;
    mentionOpen = mentionHits.length > 0;
  }

  /** Replace the `@query` under the caret with the chosen name, and leave the caret
   *  after it with a trailing space so the next word is not glued on. */
  function acceptMention(c: MentionCandidate): void {
    const el = box;
    if (!el) return;
    const shown = c.name?.trim() || (c.handle ? `@${c.handle}` : '');
    if (!shown) return;
    const anchor = shown.startsWith('@') ? shown : `@${shown}`;
    const caret = el.selectionStart ?? 0;
    const next = `${replyText.slice(0, mentionAt)}${anchor} ${replyText.slice(caret)}`;
    replyText = next;
    if (c.externalId && !picked.some((p) => p.externalId === c.externalId)) picked = [...picked, c];
    saved = false;
    saveError = null;
    closeMentions();
    const pos = mentionAt + anchor.length + 1;
    void tick().then(() => {
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  }

  /** Arrow keys, Enter and Tab belong to the menu while it is open — and to the
   *  textarea the rest of the time, so a reply with a newline in it still works. */
  function onBoxKeydown(e: KeyboardEvent): void {
    if (!mentionOpen || mentionHits.length === 0) {
      // Enter sends only once the menu has declined it, so picking a name never
      // publishes the half-written line it was picked into.
      if (submitOnEnter && e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void save();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      mentionIndex = (mentionIndex + 1) % mentionHits.length;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      mentionIndex = (mentionIndex - 1 + mentionHits.length) % mentionHits.length;
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      const pick = mentionHits[mentionIndex];
      if (pick) {
        e.preventDefault();
        acceptMention(pick);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeMentions();
    }
  }

  function onReplyInput(v: string): void {
    replyText = v;
    saved = false;
    saveError = null;
    const el = box;
    const hit = el && mentionable ? mentionUnderCaret(el) : null;
    if (!hit) {
      closeMentions();
      return;
    }
    mentionAt = hit.at;
    void refreshMentions(hit.q);
  }

  /**
   * Can the drafter be asked about THIS target?
   *
   * Not for a DM. `POST …/moderation/suggestions` declares `target.kind` as an enum of
   * exactly `post|comment` and `target.id` as an integer, so a channel's external_id
   * is refused at the schema boundary before any handler sees it — there is no request
   * this client could send that would work. Issue #168 asks for the DM twin; when it
   * lands this condition is the only thing that changes.
   */
  /**
   * The AI draft is offered for a BLANK box only.
   *
   * It replaces whatever is in the box, so beside half a written sentence it is a
   * button that destroys work — and it was taking the width that pushed the send
   * button onto a second line. Once you are typing, the only thing left to do is
   * send. It stays visible while a draft is streaming, because that button is also
   * where the progress is reported.
   */
  const suggestable = $derived(skoolId != null && kind !== 'dm');
  /**
   * Nothing written yet.
   *
   * An empty box and a box holding only names are the same state: no answer. Comparing
   * against `SEED` was not enough — clearing the box by hand left it matching neither
   * the seed nor "empty", so Suggest never came back. `isOnlyMentions` asks the real
   * question, with the same matcher that decides what becomes a mention on send.
   */
  const blank = $derived(isOnlyMentions(replyText, [...picked, ...participants]));
  const offerSuggest = $derived(suggestable && (suggesting || blank));

  /**
   * The draft, minus the mention the box is already holding.
   *
   * NOTHING FORBIDS THE MODEL FROM OPENING WITH ONE. The prompt tells it who "you" are
   * so it does not answer itself in the third person, and says nothing about how to
   * address the member — so it sometimes writes "@Name" and sometimes "Hey Name", and
   * we prepend `SEED` either way. The first case put the mention in twice.
   *
   * Fixing it HERE rather than in the prompt, because a model instruction is a request
   * and this is a guarantee: the seed is ours, so removing our own duplicate is ours
   * too, and it holds whatever the model does next.
   *
   * Works mid-stream. `@Consta` is a prefix of the mention that is coming, so it is
   * held back rather than shown and then yanked; a mention of somebody ELSE is not a
   * prefix of ours and survives untouched, because addressing a third party is a real
   * thing to write.
   */
  function withoutDuplicateOpener(draft: string): string {
    if (!opener) return draft;
    const lead = draft.trimStart();
    if (!lead.startsWith('@')) return draft;
    const mention = `@${opener}`;
    const low = lead.toLowerCase();
    const target = mention.toLowerCase();
    // Still arriving, and so far it matches: show nothing rather than a half name.
    if (target.startsWith(low)) return '';
    if (!low.startsWith(target)) return draft;
    // Ours, in full. Drop it and whatever punctuation was addressing us with it.
    return lead.slice(mention.length).replace(/^[,:;\s]+/, '');
  }

  async function suggest(): Promise<void> {
    if (!suggestable || skoolId == null || kind === 'dm' || suggesting) return;
    suggesting = true;
    suggestError = null;
    saveError = null;
    // KEEP THE MENTION. The draft is the body of the reply, not the whole of it — the
    // box opened addressed to someone and streaming over that would quietly drop them
    // from the reply that gets sent.
    replyText = SEED;
    saved = false;
    sources = [];
    ui().track('suggest_used', { kind });
    // Accumulated separately so the de-duplication sees the whole draft on every
    // delta. Appending straight to `replyText` could only ever inspect one token.
    let draft = '';
    try {
      await streamSuggestedReply(
        skoolId,
        kind,
        Number(targetId),
        (delta) => {
          draft += delta;
          replyText = SEED + withoutDuplicateOpener(draft);
        },
        {
          voiceMemberId: selectedVoice ?? undefined,
          onSources: (s) => {
            sources = s;
          },
        },
      );
    } catch (err: unknown) {
      suggestError = reason(err, 'Could not suggest a reply.');
    } finally {
      suggesting = false;
    }
  }

  /**
   * One line until there is more than one line.
   *
   * A reply box that opens four rows tall reads as a form to fill in; most replies here
   * are a sentence. It grows to fit what is typed and stops at `max-h-64`, after which
   * it scrolls — so a long reply never pushes the send button off the screen.
   */
  function grow(el: HTMLTextAreaElement): void {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }

  /**
   * HUNG OFF THE VALUE, NOT OFF `oninput` — which is the bug this replaces.
   *
   * The height was recomputed in the input handler, so it tracked TYPING and nothing
   * else. Every other way this box gets filled is a plain assignment to `replyText`
   * and fires no input event: a generated suggestion (the reported case — a
   * paragraph-long draft landing in a one-row box, scrollable but visually a single
   * line), a mention rewrite, the reset to `SEED`, and the clear after a send, which
   * left the box tall over an empty draft.
   *
   * Reading `replyText` here makes the height a function of the CONTENT, so it cannot
   * matter how the content arrived. That is also why `oninput` no longer calls
   * `grow()`: typing goes through `onReplyInput` and lands on the same state, so a
   * second call site would be a second thing to remember.
   *
   * Effects run after the DOM is updated, so `scrollHeight` is measured against the
   * value already painted rather than the one being replaced.
   */
  $effect(() => {
    const el = box;
    void replyText;
    if (el) grow(el);
  });

  /**
   * Is the box still in view?
   *
   * A rect measured on scroll, not an `IntersectionObserver`. The observer is the
   * tidier tool and it did not fire here — the composer is remounted by a `{#key}` on
   * every change of target, so the element this has to watch is replaced underneath it
   * repeatedly, and re-observing on each swap is exactly the bookkeeping the observer
   * was supposed to save. A rect is re-read from whatever `root` currently is.
   *
   * `capture: true` on window, because scroll does not bubble: the dashboard scrolls
   * the page and the side panel scrolls its own document, and a capturing listener
   * hears both without either surface having to say which it is.
   */
  $effect(() => {
    const el = root;
    if (!el || !jumpWhenOffscreen) return;
    const check = (): void => {
      const r = el.getBoundingClientRect();
      // A sliver peeking in is out of reach in practice, so the margin is deliberate.
      offscreen = r.bottom < 48 || r.top > window.innerHeight - 48;
      // Horizontal position is valid even while the box is vertically out of view,
      // which is the only state this is read in.
      anchorLeft = Math.max(8, Math.round(r.left));
    };
    check();
    window.addEventListener('scroll', check, { passive: true, capture: true });
    window.addEventListener('resize', check);
    /**
     * SCROLL AND RESIZE ARE NOT THE ONLY WAYS THIS BOX MOVES, which is the bug this
     * observer exists to close. Everything above the composer can change height after
     * mount without either event firing: a video thumbnail or image finishing layout,
     * "Show the whole post" expanding, the thread rendering its comments. The rect was
     * read once on mount — when the card was still short and the box genuinely was
     * below the fold — and never re-read, so the jump button stayed on screen pointing
     * at a composer sitting in plain view a few hundred pixels above it. Reported from
     * the panel with the reply box, its Back/Skip row and the button all visible at
     * once, which is the state that makes it obvious the flag is stale rather than
     * wrong.
     *
     * The observer is only a CHANGE DETECTOR here — `check()` above stays the single
     * definition of "offscreen", margin and all, so this cannot drift from it. That is
     * also why `IntersectionObserver` is safe now where the file's own note rejected it
     * as the primary mechanism: nothing depends on its thresholds, and the remount
     * churn it warned about is already handled, because this whole effect re-runs when
     * `{#key}` swaps `root` and re-observes the new element for free.
     */
    const io = new IntersectionObserver(check, { threshold: [0, 0.01, 1] });
    io.observe(el);
    return () => {
      window.removeEventListener('scroll', check, true);
      window.removeEventListener('resize', check);
      io.disconnect();
    };
  });

  /** Take them back to it, and put the cursor in it — arriving at a box you still have
   *  to click is half an answer. */
  function jumpToBox(): void {
    root?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => box?.focus(), 260);
  }

  async function save(): Promise<void> {
    if (!onSubmit || saving) return;
    saving = true;
    saveError = null;
    try {
      /**
       * THE MENTION IS REWRITTEN HERE, not by whoever sends.
       *
       * `[@Name](obj://user/<id>)` is the only form Skool renders as a mention, and
       * only one of the four send paths was producing it — the Helper's direct post.
       * Everything routed through the outbox went out with a literal "@Name": the
       * dashboard dropped the candidates on the floor, and the drain's own
       * `linkMentions` is a documented no-op because `mentions` is absent on a leased
       * row. So a reply that named somebody never notified them.
       *
       * Doing it here makes that unforgettable — a host receives text that is already
       * correct — and it is safe to do twice: `linkMentions` walks existing links and
       * only scans the plain text between them.
       */
      const body = mentionable ? linkMentions(replyText, picked) : replyText;
      const reported = await onSubmit(body, picked);
      said = typeof reported === 'string' && reported ? reported : null;
      saved = true;
      ui().track('reply_queued', { kind });
      replyText = '';
    } catch (err: unknown) {
      saveError = reason(err, 'Could not send this reply.');
    } finally {
      saving = false;
    }
  }

  function sourceLabel(s: SourceRef): string {
    return s.title || s.course_title || s.url || 'Source';
  }
</script>

<!-- NO FRAME OF ITS OWN. The textarea below is already a bordered control, so a box
     around it drew the same edge twice — and this sits inside a thread that had a box
     for the post and a box for every comment. One border, on the thing you type in. -->
<div bind:this={root}>
  <div class="relative">
    <textarea
      bind:this={box}
      use:autofocus
      rows="1"
      class="redact-hint textarea textarea-bordered max-h-64 min-h-0 w-full resize-none overflow-y-auto bg-base-100 py-2.5 text-sm leading-relaxed"
      placeholder={suggesting ? 'Drafting a suggestion…' : placeholder}
      value={replyText}
      disabled={suggesting}
      onfocus={handleFocus}
      onkeydown={onBoxKeydown}
      onblur={() => setTimeout(closeMentions, 120)}
      oninput={(e) => onReplyInput((e.currentTarget as HTMLTextAreaElement).value)}
    ></textarea>

    <!-- The mention menu. `onmousedown` with `preventDefault` rather than a click:
         the textarea's blur would otherwise close the menu before a click could land,
         and the pick would be lost to a race the owner cannot see. -->
    {#if mentionOpen}
      <ul
        class="absolute left-1 right-1 top-full z-30 mt-1 max-h-56 overflow-y-auto rounded-box border hair bg-base-100 py-1 shadow-lg"
        role="listbox"
        aria-label="Mention someone"
      >
        {#each mentionHits as c, i (c.externalId)}
          <li>
            <button
              type="button"
              role="option"
              aria-selected={i === mentionIndex}
              class="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs
                     {i === mentionIndex ? 'bg-primary/10' : 'hover:bg-base-200/70'}"
              onmousedown={(e) => {
                e.preventDefault();
                acceptMention(c);
              }}
            >
              <Avatar src={c.pictureUrl ?? null} name={c.name ?? c.handle ?? ''} size={20} />
              <span class="redact min-w-0 flex-1 truncate font-medium">{c.name ?? c.handle}</span>
              {#if c.handle && c.name}
                <span class="redact shrink-0 truncate font-mono text-[10px] opacity-55">@{c.handle}</span>
              {/if}
            </button>
          </li>
        {/each}
      </ul>
    {/if}
  </div>
  <div class="mt-2.5 flex flex-wrap items-center gap-2">
    {#if leading}{@render leading()}{/if}
    {#if saved}<span class="text-xs font-medium text-success">{said ?? doneLabel} ✓</span>{/if}
    <!-- Right, in the order they are reached for: draft it, then send it. Send is the
         last thing on the line because it is the last thing you do. -->
    <!-- `flex-wrap` and `justify-end`: at 340px the voice picker, Suggest and the send
         button do not fit on one line, and without a wrap the send overlapped Suggest
         rather than dropping below it. -->
    <div class="ml-auto flex flex-wrap items-center justify-end gap-2">
      {#if oncancel}
        <button class="btn btn-ghost btn-sm text-base-content/68" onclick={oncancel}>Cancel</button>
      {/if}
      <!-- Hidden entirely for a DM rather than shown-and-refused: a control that
           always errors is worse than one that is not there. See `suggestable`. -->
      <div class="relative {offerSuggest ? '' : 'hidden'}" bind:this={pickerEl}>
        <div class="join">
          {#if voices.length}
            <button
              type="button"
              class="btn btn-outline btn-sm join-item gap-1 px-1.5 font-medium normal-case"
              onclick={() => (voiceMenuOpen = !voiceMenuOpen)}
              disabled={suggesting}
              aria-haspopup="listbox"
              aria-expanded={voiceMenuOpen}
              aria-label="Draft in {voiceLabel}'s voice"
              title="Drafting in {voiceLabel}'s voice"
            >
              <!-- THE FACE, NOT THE NAME. Whose voice this drafts in is a setting people
                   change once; spelling it out cost more width than the Suggest button it
                   is attached to. The name is on the tooltip and in the open menu. -->
              <Avatar src={selectedVoiceOption?.picture_profile ?? null} name={voiceLabel} size={20} />
              <svg class="h-3 w-3 opacity-55 transition-transform {voiceMenuOpen ? 'rotate-180' : ''}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
            </button>
          {/if}
          <button
            class="btn btn-outline btn-sm join-item gap-1.5"
            onclick={suggest}
            disabled={suggesting}
            title="Draft a reply in your community's voice, from your indexed content"
          >
            {#if suggesting}
              <span class="loading loading-spinner loading-xs"></span> Thinking…
            {:else}
              <svg class="h-3.5 w-3.5 text-primary" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.9 5.6L19.5 9.5l-5.6 1.9L12 17l-1.9-5.6L4.5 9.5l5.6-1.9L12 2z" /></svg>
              Suggest
            {/if}
          </button>
        </div>

        {#if voiceMenuOpen && voices.length}
          <ul
            class="absolute bottom-full right-0 z-30 mb-1.5 max-h-64 w-56 overflow-auto rounded-box border border-base-300 bg-base-100 p-1 shadow-lg"
            role="listbox"
            aria-label="Voice to write in"
          >
            {#each voices as v (v.member_id)}
              {@const on = v.member_id === selectedVoice}
              <li>
                <button
                  type="button"
                  role="option"
                  aria-selected={on}
                  class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors {on ? 'bg-primary/10' : 'hover:bg-base-200'}"
                  onclick={() => chooseVoice(v.member_id)}
                >
                  <Avatar src={v.picture_profile} name={v.name || '@' + v.handle} size={26} />
                  <span class="min-w-0 flex-1">
                    <span class="redact block truncate text-sm font-medium leading-tight">{v.name || '@' + v.handle}</span>
                    {#if v.role}<span class="block text-[0.7rem] capitalize leading-tight text-base-content/55">{v.role}</span>{/if}
                  </span>
                  {#if on}<svg class="h-4 w-4 shrink-0 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12" /></svg>{/if}
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      </div>

      <!--
        ALWAYS PRESENT, DISABLED WHEN THERE IS NOTHING TO SEND.

        This used to appear only once something was typed, on the reasoning that a
        disabled button is a thing you can aim at and fail to press. What that actually
        produced is worse: the action row RESHUFFLES on the first keystroke, because
        Suggest was holding the space and now has to give it up. The control someone is
        moving towards moves out from under the cursor, and an empty composer gives no
        clue that sending is even how this ends.

        A dead button is legible; a button that materialises is not. It is disabled
        rather than absent, and the row's geometry is the same before and after typing.

        `saving` keeps it live through the send, when the box has been cleared but the
        request has not come back — otherwise it would grey out exactly when the person
        is watching to see that it worked.

        `title`, not DaisyUI's `.tooltip`: that renders the tip through a `::before`
        sized to `max-content`, which sits outside the DOM but inside the document's
        scroll width — a 40-character tip was adding 75px of horizontal scroll to a
        360px panel, as blank space to the right of everything on it.

        And no avatar, and no name. Who it goes out as belongs in the title, not on the
        face of a button whose job is one word — it was a second identity beside the
        voice picker's, and the two meant different things.
      -->
      {#if onSubmit}
        <button
          class="btn btn-primary btn-sm shrink-0 gap-1.5"
          onclick={save}
          disabled={(blank && !saving) || saving || suggesting}
          aria-label={authorName ? `${submitLabel} as ${authorName}` : submitLabel}
          title={postTip}
        >
          {saving ? 'Working…' : submitLabel}
        </button>
      {/if}
    </div>
  </div>

  {#if warn}
    <!-- Only when a reply won't go out as-is. Saving is never blocked: the Helper
         itself refuses to post under the wrong account. -->
    <div class="mt-2 flex items-center gap-2 border-t hair pt-2 text-[0.68rem] leading-snug text-warning">
      <span class="inline-block h-2 w-2 shrink-0 rounded-full bg-warning"></span>
      <span class="min-w-0 flex-1">{warn}</span>
    </div>
  {/if}
  <!-- Both wrap: these carry whole server sentences, and a 340px panel would
       otherwise clip one to a single unreadable line. -->
  {#if saveError}<div class="mt-1 whitespace-normal break-words text-xs text-error" role="alert">{saveError}</div>{/if}
  {#if suggestError}<div class="mt-1 whitespace-normal break-words text-xs text-error">{suggestError}</div>{/if}
  {#if sources.length}
    <div class="mt-2">
      <button
        type="button"
        class="btn btn-ghost btn-xs gap-1 px-1 text-base-content/72 hover:text-base-content/80"
        onclick={() => (showSources = !showSources)}
        aria-expanded={showSources}
      >
        <svg class="h-3 w-3 transition-transform {showSources ? 'rotate-90' : ''}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
        Sources used ({sources.length})
      </button>
      {#if showSources}
        <ul class="mt-1 space-y-0.5 pl-4">
          {#each sources as s, i (i)}
            <li class="text-xs leading-relaxed">
              {#if s.url}
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="link link-hover text-primary/80"
                  onclick={(e) => followLink(e, s.url ?? '')}
                >{sourceLabel(s)}</a>
              {:else}
                <span class="text-base-content/72">{sourceLabel(s)}</span>
              {/if}
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  {/if}
</div>

{#if offscreen && jumpWhenOffscreen && onSubmit}
  <!-- The way back, once the box is behind you.
       Pinned to the viewport rather than the thread, because the whole point is that
       the thread has scrolled past it. It shows the reply arrow the rest of the thread
       uses for the same act, so it cannot be mistaken for "next" — and it changes
       wording when there is a draft to come back TO, which is the case where losing
       the box actually costs something.

       BOTTOM-LEFT OF THE CONTENT COLUMN. The bottom-RIGHT corner belongs to the deck's
       "Done with Post"; the two appear under overlapping conditions and a shared corner
       means one silently covers the other at side-panel width. The horizontal offset is
       a value rather than `left-5` because the viewport is not the content area in
       every host — see the note on `anchorLeft`. -->
  <button
    type="button"
    class="btn btn-primary btn-sm fixed bottom-5 z-40 gap-1.5 rounded-full shadow-lg shadow-black/20"
    style="left: var(--sg-float-left, {anchorLeft}px)"
    onclick={jumpToBox}
  >
    <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" /></svg>
    {replyText.trim() ? 'Back to your reply' : 'Write a reply'}
  </button>
{/if}
